/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import Vue from "vue";
import { IWssEvent, IWssConnectedPayload } from "./wss-event.interface";

/**
 * Type-safe event handler for WSS events
 */
export type WssEventHandler<T = Record<string, unknown>> = (event: IWssEvent<T>) => void;

/**
 * Handler for connection events
 */
export type WssConnectedHandler = (payload: IWssConnectedPayload) => void;

/**
 * Handler for error events
 */
export type WssErrorHandler = (error: Error) => void;

/**
 * Scalable event emitter for WebSocket events.
 * Uses Vue's event system for compatibility with Vue 2.
 *
 * Business logic listeners can subscribe to specific events using the event name
 * from the backend (e.g., "report.created", "message.updated").
 *
 * @example
 * ```typescript
 * // Subscribe to a specific event
 * WssEventEmitter.on("report.created", (event) => {
 *   console.log("Report created:", event.data);
 * });
 *
 * // Subscribe to all partner events
 * WssEventEmitter.onAny((event) => {
 *   console.log("Any event:", event);
 * });
 *
 * // Unsubscribe
 * WssEventEmitter.off("report.created", handler);
 * ```
 */
class WssEventEmitterClass {
  private readonly eventBus = new Vue();

  /**
   * Event name for catching all partner events
   */
  private static readonly ALL_EVENTS = "*";

  /**
   * Event name for connection events
   */
  private static readonly CONNECTED = "wss:connected";

  /**
   * Event name for error events
   */
  private static readonly ERROR = "wss:error";

  /**
   * Event name for disconnect events
   */
  private static readonly DISCONNECTED = "wss:disconnected";

  /**
   * Subscribe to a specific event type.
   * The event name corresponds to the backend event name (e.g., "report.created").
   *
   * @param eventName The event name to subscribe to
   * @param handler The handler function to call when the event is received
   */
  on<T = Record<string, unknown>>(eventName: string, handler: WssEventHandler<T>): void {
    this.eventBus.$on(eventName, handler);
  }

  /**
   * Subscribe to all partner events.
   * This is useful for logging or debugging purposes.
   *
   * @param handler The handler function to call for any event
   */
  onAny<T = Record<string, unknown>>(handler: WssEventHandler<T>): void {
    this.eventBus.$on(WssEventEmitterClass.ALL_EVENTS, handler);
  }

  /**
   * Subscribe to connection events.
   *
   * @param handler The handler function to call when connected
   */
  onConnected(handler: WssConnectedHandler): void {
    this.eventBus.$on(WssEventEmitterClass.CONNECTED, handler);
  }

  /**
   * Subscribe to error events.
   *
   * @param handler The handler function to call on error
   */
  onError(handler: WssErrorHandler): void {
    this.eventBus.$on(WssEventEmitterClass.ERROR, handler);
  }

  /**
   * Subscribe to disconnect events.
   *
   * @param handler The handler function to call when disconnected
   */
  onDisconnected(handler: (reason: string) => void): void {
    this.eventBus.$on(WssEventEmitterClass.DISCONNECTED, handler);
  }

  /**
   * Unsubscribe from a specific event type.
   *
   * @param eventName The event name to unsubscribe from
   * @param handler The handler function to remove (optional, removes all if not provided)
   */
  off<T = Record<string, unknown>>(eventName: string, handler?: WssEventHandler<T>): void {
    this.eventBus.$off(eventName, handler);
  }

  /**
   * Unsubscribe from all events subscription.
   *
   * @param handler The handler function to remove
   */
  offAny<T = Record<string, unknown>>(handler?: WssEventHandler<T>): void {
    this.eventBus.$off(WssEventEmitterClass.ALL_EVENTS, handler);
  }

  /**
   * Unsubscribe from connection events.
   *
   * @param handler The handler function to remove
   */
  offConnected(handler?: WssConnectedHandler): void {
    this.eventBus.$off(WssEventEmitterClass.CONNECTED, handler);
  }

  /**
   * Unsubscribe from error events.
   *
   * @param handler The handler function to remove
   */
  offError(handler?: WssErrorHandler): void {
    this.eventBus.$off(WssEventEmitterClass.ERROR, handler);
  }

  /**
   * Unsubscribe from disconnect events.
   *
   * @param handler The handler function to remove
   */
  offDisconnected(handler?: (reason: string) => void): void {
    this.eventBus.$off(WssEventEmitterClass.DISCONNECTED, handler);
  }

  /**
   * Subscribe to an event once. The handler will be removed after the first call.
   *
   * @param eventName The event name to subscribe to
   * @param handler The handler function to call once
   */
  once<T = Record<string, unknown>>(eventName: string, handler: WssEventHandler<T>): void {
    this.eventBus.$once(eventName, handler);
  }

  /**
   * Emit a partner event. Called internally by the WebSocket service.
   *
   * @param event The WSS event received from the backend
   */
  emit<T = Record<string, unknown>>(event: IWssEvent<T>): void {
    // Emit to specific event listeners
    this.eventBus.$emit(event.event, event);

    // Emit to "all events" listeners
    this.eventBus.$emit(WssEventEmitterClass.ALL_EVENTS, event);
  }

  /**
   * Emit a connected event. Called internally by the WebSocket service.
   *
   * @param payload The connected payload
   */
  emitConnected(payload: IWssConnectedPayload): void {
    this.eventBus.$emit(WssEventEmitterClass.CONNECTED, payload);
  }

  /**
   * Emit an error event. Called internally by the WebSocket service.
   *
   * @param error The error
   */
  emitError(error: Error): void {
    this.eventBus.$emit(WssEventEmitterClass.ERROR, error);
  }

  /**
   * Emit a disconnected event. Called internally by the WebSocket service.
   *
   * @param reason The disconnect reason
   */
  emitDisconnected(reason: string): void {
    this.eventBus.$emit(WssEventEmitterClass.DISCONNECTED, reason);
  }

  /**
   * Remove all event listeners. Useful for cleanup.
   */
  removeAllListeners(): void {
    this.eventBus.$off();
  }
}

/**
 * Singleton instance of the WSS event emitter.
 * Use this to subscribe to WebSocket events throughout the application.
 */
export const WssEventEmitter = new WssEventEmitterClass();
